import { useEffect, type JSX } from "react";
import { useBranchPreview } from "../../../api/hooks/useBranchPreview";
import type { UIError } from "../../../errors/types";

export type UnexploredBranchIndicatorProps = {
  /** 起点となる worldline (= playline) の id。 */
  worldlineId: string;
  /** 未踏 choice が属する scene の id。 */
  sceneId: string;
  /** 未踏 choice の id。 */
  choiceId: string;
  /** 未踏 choice の表示テキスト (aria-label 用)。 */
  choiceText: string;
  /**
   * AI 提案取得失敗時に親へ委譲するコールバック。toast 等で案内する用途を想定。
   * 未指定時はエラーは indicator 内にのみ保持される (後続タップで再試行可)。
   */
  onPreviewError?: (err: UIError) => void;
};

/**
 * worldline-palimpsest (Phase 3-1, Task 5.2) 未踏分岐 "?" インジケーター。
 *
 * 初期は "?" を描画し、tap で useBranchPreview を発火して AI 提案 (280 字要約 +
 * keywords) を取得する。成功時は「まだ起きていない仮説として」summary + keywords を
 * 破線枠で表示 (Req 5.3 仮説マーキング)。
 *
 * エラー時は "?" の状態に戻し、`onPreviewError` コールバックで親へ委譲する
 * (Req 5.4 / 12.4: 失敗時は "?" のまま、toast で案内、他操作に影響しない)。
 */
export function UnexploredBranchIndicator({
  worldlineId,
  sceneId,
  choiceId,
  choiceText,
  onPreviewError,
}: UnexploredBranchIndicatorProps): JSX.Element {
  const preview = useBranchPreview();
  const { data, error, isPending, isSuccess, isError, mutate, reset } = preview;

  // エラー発生時は親に UIError を委譲 + 内部 state を reset することで "?" 表示に戻す
  // (Req 5.4 / 12.4: 失敗 = "?" 維持、再試行可)
  useEffect(() => {
    if (isError && error !== null && error !== undefined) {
      onPreviewError?.(error);
      reset();
    }
  }, [isError, error, onPreviewError, reset]);

  const handleTap = (): void => {
    if (isPending) return;
    mutate({ worldlineId, sceneId, choiceId });
  };

  const ariaLabelBase = `未踏の分岐「${choiceText}」`;

  if (isSuccess && data !== undefined) {
    return (
      <div
        data-testid={`palimpsest-unexplored-preview-${choiceId}`}
        className="inline-flex flex-col items-start gap-1 px-2 py-1 rounded border border-dashed border-indigo-400/60 bg-indigo-950/40 text-indigo-100 text-[11px] max-w-[240px]"
        role="note"
        aria-label={`${ariaLabelBase} の AI 仮説`}
      >
        <span className="text-[9px] uppercase tracking-wider text-indigo-300">
          仮説 (まだ起きていない)
        </span>
        <p className="leading-snug">{data.summary}</p>
        {data.keywords.length > 0 && (
          <ul
            data-testid={`palimpsest-unexplored-keywords-${choiceId}`}
            className="flex flex-wrap gap-1"
          >
            {data.keywords.map((k, i) => (
              <li
                key={`${i}-${k}`}
                className="px-1.5 py-0.5 rounded bg-indigo-900/60 text-indigo-200 text-[10px]"
              >
                {k}
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={() => reset()}
          className="text-[10px] text-indigo-300 hover:text-indigo-100 underline"
        >
          閉じる
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid={`palimpsest-unexplored-indicator-${choiceId}`}
      data-pending={isPending ? "true" : undefined}
      className={[
        "inline-flex items-center justify-center w-6 h-6 rounded-full border text-[11px] font-mono transition-colors focus:outline-none focus-visible:ring-2",
        "border-slate-600 bg-slate-800/40 text-slate-300",
        isPending ? "opacity-60 cursor-wait" : "hover:bg-slate-700",
      ].join(" ")}
      aria-label={`${ariaLabelBase} (提案を見る)`}
      onClick={handleTap}
      disabled={isPending}
    >
      {isPending ? "…" : "?"}
    </button>
  );
}
